/**
 * Engine alarm rollup from notifications.propulsion.* (mirrors backend helper).
 */

const PROPULSION_NOTIF_RE = /^notifications\.propulsion\.(port|starboard)\.(.+)$/;

const ALARM_LABELS = {
  checkEngine: 'Check engine',
  chargeIndicator: 'Charge indicator',
  commError: 'Communication error',
  eGRSystem: 'EGR system',
  emergencyStopMode: 'Emergency stop',
  highBoostPressure: 'High boost pressure',
  lowCoolantLevel: 'Low coolant',
  lowFuelPressure: 'Low fuel pressure',
  lowOilLevel: 'Low oil level',
  lowOilPressure: 'Low oil pressure',
  lowSystemVoltage: 'Low system voltage',
  maintenanceNeeded: 'Maintenance needed',
  neutralStartProtect: 'Neutral start protect',
  overTemperature: 'Over temperature',
  powerReduction: 'Power reduction',
  preheatIndicator: 'Preheat',
  revLimitExceeded: 'Rev limit exceeded',
  shuttingDown: 'Shutting down',
  subOrSecondaryThrottle: 'Sub throttle',
  throttlePositionSensor: 'Throttle sensor',
  warningLevel1: 'Warning level 1',
  warningLevel2: 'Warning level 2',
  waterFlow: 'Water flow',
  waterInFuel: 'Water in fuel',
};

const NORMAL_STATES = new Set(['normal', 'cleared', 'ok', 'off', 'idle']);
const WARNING_STATES = new Set(['warning', 'caution']);
const ALARM_STATES = new Set(['alarm', 'alert', 'fault', 'critical', 'emergency']);

function parseNumeric(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeNotificationState(raw) {
  if (raw === null || raw === undefined) {
    return { state: 'unknown', message: '', raw };
  }
  if (typeof raw === 'object' && raw !== null) {
    const state = String(raw.state ?? raw.value ?? '').trim();
    const message = raw.message != null ? String(raw.message).trim() : '';
    return { state, message, raw };
  }
  return { state: String(raw).trim(), message: '', raw };
}

function classifyState(state) {
  const s = String(state || '').trim().toLowerCase();
  if (!s) return 'unknown';
  if (NORMAL_STATES.has(s)) return 'normal';
  if (WARNING_STATES.has(s)) return 'warning';
  if (ALARM_STATES.has(s)) return 'alarm';
  if (s.includes('warn')) return 'warning';
  if (s.includes('alarm') || s.includes('alert') || s.includes('fault')) return 'alarm';
  return 'unknown';
}

function labelForAlarmKey(key) {
  return ALARM_LABELS[key] || key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function readEntryValue(entry) {
  if (entry === null || entry === undefined) return null;
  if (typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'value')) {
    return entry.value;
  }
  return entry;
}

function readEntryTimestamp(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const ts = entry.timestamp || entry.updated_at;
  if (!ts) return null;
  const d = ts instanceof Date ? ts : new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

function rpmFromFlat(signalkFlat, engine) {
  const path = `propulsion.${engine}.revolutions`;
  const entry = signalkFlat[path];
  if (!entry) return 0;
  const n = parseNumeric(readEntryValue(entry));
  if (n === null) return 0;
  return n < 15 ? n * 60 : n;
}

function enginesLikelyRunning(signalkFlat) {
  return rpmFromFlat(signalkFlat, 'port') > 200 || rpmFromFlat(signalkFlat, 'starboard') > 200;
}

function sortIssues(issues) {
  const priority = (key) => {
    if (key === 'checkEngine') return 0;
    if (key === 'warningLevel1') return 1;
    if (key === 'warningLevel2') return 2;
    return 10;
  };
  return [...issues].sort((a, b) => {
    const pa = priority(a.key);
    const pb = priority(b.key);
    if (pa !== pb) return pa - pb;
    return a.label.localeCompare(b.label);
  });
}

export function buildSignalkFlatMap({ liveData, signalKFlat, signalKData }) {
  const out = {};
  const mergeFlat = (flat) => {
    for (const [path, entry] of Object.entries(flat || {})) {
      if (!path || !path.includes('.')) continue;
      out[path] = {
        value: readEntryValue(entry),
        timestamp: readEntryTimestamp(entry) || entry?.timestamp,
      };
    }
  };
  mergeFlat(signalKFlat);
  mergeFlat(liveData);

  const walk = (node, prefix = '') => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return;
    const isLeaf =
      Object.prototype.hasOwnProperty.call(node, 'value') &&
      !Object.prototype.hasOwnProperty.call(node, 'values');
    if (isLeaf && prefix && !out[prefix]) {
      out[prefix] = {
        value: node.value,
        timestamp: node.timestamp,
      };
      return;
    }
    for (const [key, val] of Object.entries(node)) {
      if (key.startsWith('$')) continue;
      const path = prefix ? `${prefix}.${key}` : key;
      if (val && typeof val === 'object' && Object.prototype.hasOwnProperty.call(val, 'value')) {
        if (!out[path]) {
          out[path] = { value: val.value, timestamp: val.timestamp };
        }
      } else if (val && typeof val === 'object') {
        walk(val, path);
      }
    }
  };
  walk(signalKData || {});
  return out;
}

export function summarizePropulsionAlarms(signalkFlat, options = {}) {
  const freshMinutes = options.freshMinutes ?? 5;
  const engines = { port: [], starboard: [] };
  let normalCount = 0;
  let pathCount = 0;
  let latestTs = null;

  for (const [path, entry] of Object.entries(signalkFlat || {})) {
    const m = path.match(PROPULSION_NOTIF_RE);
    if (!m) continue;
    const [, engine, alarmKey] = m;
    pathCount += 1;

    const ts = readEntryTimestamp(entry);
    if (ts && (!latestTs || ts > latestTs)) latestTs = ts;

    const normalized = normalizeNotificationState(readEntryValue(entry));
    const severity = classifyState(normalized.state);

    if (severity === 'normal') {
      normalCount += 1;
      continue;
    }
    if (severity === 'unknown') continue;

    engines[engine].push({
      key: alarmKey,
      label: labelForAlarmKey(alarmKey),
      state: severity,
      message: normalized.message,
      path,
      rawState: normalized.state,
    });
  }

  const portIssues = sortIssues(engines.port);
  const starboardIssues = sortIssues(engines.starboard);
  const allIssues = [...portIssues, ...starboardIssues];
  const enginesRunning = enginesLikelyRunning(signalkFlat);

  const ageMs = latestTs ? Date.now() - latestTs.getTime() : null;
  const dataFresh = ageMs !== null && ageMs <= freshMinutes * 60 * 1000;
  const dataStale = ageMs !== null && ageMs > freshMinutes * 60 * 1000;

  let status = 'no_data';
  if (pathCount === 0) {
    status = 'no_data';
  } else if (allIssues.some((i) => i.state === 'alarm')) {
    status = 'alert';
  } else if (allIssues.some((i) => i.state === 'warning')) {
    status = 'warning';
  } else if (dataStale && enginesRunning) {
    status = 'stale';
  } else if (pathCount > 0 && allIssues.length === 0) {
    status = dataStale && !enginesRunning ? 'idle' : 'clear';
  }

  return {
    status,
    enginesRunning,
    dataFresh,
    dataStale,
    lastUpdate: latestTs,
    port: portIssues,
    starboard: starboardIssues,
    issueCount: allIssues.length,
    normalCount,
    pathCount,
  };
}

export function statusMeta(status) {
  switch (status) {
    case 'alert':
      return { label: 'Alarm', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' };
    case 'warning':
      return { label: 'Warning', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' };
    case 'stale':
      return { label: 'Stale data', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' };
    case 'clear':
      return { label: 'All clear', color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' };
    case 'idle':
      return { label: 'Engines off', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.12)' };
    default:
      return { label: 'No data', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.12)' };
  }
}
